import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@headlessui/react";
import { PlusIcon, UsersIcon } from "@heroicons/react/24/solid";
import { isHigherThanDistrictAdmin, UserRole } from "../../app/userSlice";
import { useAppSelector } from "../../app/hooks";
import SimplePagination from "../../components/SimplePagination";
import { Loading } from "../../components/Loading";
import ModalFrame from "../../components/ModalFrame";
import SearchFilter from "../../components/input/SearchFilter";
import Select from "../../components/input/Select";
import SchoolListTable from "./SchoolListTable";
import SchoolInfoSection from "./SchoolInfoSection";
import {
	emptySchoolViewModel,
	SchoolFilter,
	SchoolListQueryModel,
	useSchoolListInfoQuery,
	useSchoolListQuery,
} from "./schoolsApiSlice";

export default function SchoolList() {
	const { role } = useAppSelector((state) => state.user);
	const { config } = useAppSelector((state) => state.site);

	const _displayLength = 10;
	const initQueryargs: SchoolListQueryModel = {
		iDisplayStart: 0,
		iDisplayLength: _displayLength,
		iSortCol_0: 0,
		sSortDir_0: "desc",
		searchString: "",
		selectedActiveSchoolFilter: "Active",
	};

	const [queryargs, setQueryargs] = useState(initQueryargs);
	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [statusFilter, setStatusFilter] = useState("Active");
	const [isFiltered, setIsFiltered] = useState(false);
	const [isAddSchoolDialogOpen, setIsAddSchoolDialogOpen] = useState(false);

	const schoolListInfoResult = useSchoolListInfoQuery();
	const schoolListInfo = schoolListInfoResult.data;

	const schoolListResult = useSchoolListQuery(queryargs);
	const schools = schoolListResult.data;

	const location = useLocation();
	const navigate = useNavigate();

	useEffect(() => {
		if (location.state != null) {
			setIsAddSchoolDialogOpen(location.state.openCreateSchoolDialog ?? false);
		}
		navigate(location, { replace: true });
	}, []);

	useEffect(() => {
		setQueryargs({
			...queryargs,
			iDisplayStart: _displayLength * page,
			searchString: searchFilter,
			selectedActiveSchoolFilter: statusFilter,
		});
	}, [page, searchFilter, statusFilter]);

	useEffect(() => {
		setIsFiltered(!!searchFilter);
		setPage(0);
	}, [searchFilter, statusFilter]);

	const handlePageChange = (page: number) => {
		setPage(page);
	};

	const handleStatusChange = (status: string) => {
		setStatusFilter(status);
	};

	return (
		<section>
			<h1>Schools</h1>
			<div className="flex justify-between items-end">
				<div className="flex gap-3 mt-4 items-end">
					{!config?.isUtahComposeSite && (
						<Select
							value={statusFilter}
							setValue={(v) => handleStatusChange(v as string)}
							label="Status"
							simpleListBoxId="statusFilter"
							options={[
								{
									value: "Active",
									label: SchoolFilter.Active,
								},
								{
									value: "Inactive",
									label: SchoolFilter.Inactive,
								},
								{
									value: "All",
									label: SchoolFilter.All,
								},
							]}
							fieldClasses="w-56"
						/>
					)}
					<SearchFilter
						id="searchBySchoolName"
						name="searchBySchoolName"
						searchFilter={searchFilter}
						setSearchFilter={setSearchFilter}
						label="Search"
						placeHolder="Search by school name..."
						className="w-64"
					/>
				</div>
				{role === UserRole.DistrictAdmin &&
					!config?.isUtahComposeSite &&
					schoolListInfo && (
						<div className="flex items-end gap-3 pr-3">
							<UsersIcon className="size-10" />
							<span className="tracking-widest text-2xl">
								<span className="text-2xl font-medium">
									{schoolListInfo.takenSeats}
								</span>
								/<span className="text-xl">{schoolListInfo.totalSeats}</span>
							</span>
						</div>
					)}
				{role && isHigherThanDistrictAdmin(role) && (
					<Button
						id="add-school-button"
						className="btn btn-primary"
						onClick={() => setIsAddSchoolDialogOpen(true)}>
						<span className="flex items-center">
							<PlusIcon className="size-4 mr-2" /> Add School
						</span>
					</Button>
				)}
			</div>
			<Loading loading={schoolListResult.isLoading} />
			{schools && schoolListInfo && <SchoolListTable schools={schools.aaData} />}
			<ModalFrame
				open={isAddSchoolDialogOpen}
				title="Add New School"
				overflow="overflow-visible"
				onClose={() => setIsAddSchoolDialogOpen(false)}>
				<SchoolInfoSection
					schoolInfo={emptySchoolViewModel}
					addingNew={true}
					setIsDialogOpen={setIsAddSchoolDialogOpen}
				/>
			</ModalFrame>
			<SimplePagination
				itemCount={schools?.iTotalDisplayRecords}
				page={page}
				pageSize={queryargs.iDisplayLength}
				totalCount={schools?.iTotalRecords}
				isFiltered={isFiltered}
				forceShow={true}
				onChange={(page) => handlePageChange(page)}
				className={"border-none"}
			/>
		</section>
	);
}
